import React from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { WorkflowVersionSummary } from './workflowGraphTypes';
import type { WorkflowNameSummary } from '../../../services/workflowConfigService';

// Caps the Open/Versions dropdown menus at a fixed height so a long list (many saved workflows,
// many saved versions) scrolls within the menu instead of growing off-screen.
const SCROLLABLE_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 240,
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': { width: 8 },
      '&::-webkit-scrollbar-thumb': { backgroundColor: 'action.disabled', borderRadius: 4 },
    },
  },
} as const;

const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>{text}</Box>}
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} tabIndex={-1}>
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

interface WorkflowToolbarProps {
  configName: string;
  onConfigNameChange: (value: string) => void;
  isReservedName: boolean;

  /** NEW — company-wide "which workflow do you want to open" picker (GET /workflow-config/names). */
  workflowNames: WorkflowNameSummary[] | null;
  workflowNamesLoading: boolean;
  workflowNamesError: string | null;
  onOpenWorkflowsMenu: () => void;
  onOpenWorkflow: (name: string) => void;

  onSave: () => void;
  saveDisabled: boolean;

  onLoad: () => void;
  loadDisabled: boolean;
  loadDisabledReason?: string;
  onValidate: () => void;
  validateDisabled: boolean;
  onDeleteSelected: () => void;
  deleteSelectedDisabled: boolean;

  versionsLoading: boolean;
  versionsError: string | null;
  versions: WorkflowVersionSummary[] | null;
  onRefreshVersions: () => void;
  /** NEW — loads a specific version onto the canvas when picked from the Versions dropdown. */
  onSelectVersion: (version: number) => void;
  selectedVersion: number | null;
  versionLoadBusy: boolean;
  versionLoadWarning: string | null;
  /** NEW — non-null while a non-latest version is loaded onto the canvas; explains that Save
   * will create a new version rather than overwrite the one currently open. */
  oldVersionNotice: string | null;

  loadError: string | null;
  validationErrors: string[] | null;
  saveMessage: string | null;
  saveError: string | null;
}

/**
 * n8n-style top toolbar: editable workflow name on the left; a Versions dropdown, Load,
 * Validate, Save, and Delete selected controls on the right. Purely presentational — every
 * handler/value is owned by WorkflowCanvasTab, so no save/load/validate logic lives here or is
 * duplicated. The "Workflows" generic/leave picker and the Dry run checkbox that used to live
 * in this toolbar were both removed — dry-run vs. live save is now chosen via the "Dry run" /
 * "Execute" buttons centered at the bottom of the canvas instead.
 *
 * NEW — Versions used to be a history icon that opened a popover panel; it's now an always-
 * enabled inline dropdown directly in the toolbar row, left of Load. Opening the dropdown
 * refreshes the version list itself (no separate icon/button needed to trigger that).
 */
const WorkflowToolbar: React.FC<WorkflowToolbarProps> = ({
  configName,
  onConfigNameChange,
  isReservedName,
  workflowNames,
  workflowNamesLoading,
  workflowNamesError,
  onOpenWorkflowsMenu,
  onOpenWorkflow,
  onSave,
  saveDisabled,
  onLoad,
  loadDisabled,
  loadDisabledReason,
  onValidate,
  validateDisabled,
  onDeleteSelected,
  deleteSelectedDisabled,
  versionsLoading,
  versionsError,
  versions,
  onRefreshVersions,
  onSelectVersion,
  selectedVersion,
  versionLoadBusy,
  versionLoadWarning,
  oldVersionNotice,
  loadError,
  validationErrors,
  saveMessage,
  saveError,
}) => {
  const latestVersion = versions && versions.length > 0 ? Math.max(...versions.map((v) => v.version)) : null;

  const handleVersionsOpen = () => {
    onRefreshVersions();
  };

  const handleVersionChange = (e: SelectChangeEvent<number>) => {
    onSelectVersion(Number(e.target.value));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Select
            value=""
            onOpen={onOpenWorkflowsMenu}
            onChange={(e) => {
              const picked = e.target.value as string;
              if (picked) onOpenWorkflow(picked);
            }}
            displayEmpty
            size="small"
            renderValue={() => <Typography variant="body2" color="text.secondary">Open…</Typography>}
            sx={{ minWidth: 150, maxWidth: 220 }}
            MenuProps={SCROLLABLE_MENU_PROPS}
          >
            {workflowNamesLoading && (
              <MenuItem disabled value="">
                <CircularProgress size={14} sx={{ mr: 1 }} /> Loading workflows…
              </MenuItem>
            )}
            {!workflowNamesLoading && (!workflowNames || workflowNames.length === 0) && (
              <MenuItem disabled value="">
                No saved workflows yet
              </MenuItem>
            )}
            {!workflowNamesLoading && workflowNames && workflowNames.map((w) => (
              <MenuItem key={w.name} value={w.name}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{w.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    v{w.version} — {w.status} · {w.created_by}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <FieldHelp text={'Browse every workflow this company has ever saved, across all channels.\nPicking one loads its latest saved version onto the canvas — same as typing its name and clicking Load.\nThis list does not include internal test runs or an unsaved "leave" demo template.'} />
        </Box>

        <TextField
          value={configName}
          onChange={(e) => onConfigNameChange(e.target.value)}
          placeholder="Untitled workflow"
          variant="standard"
          size="small"
          error={isReservedName}
          helperText={isReservedName ? 'Reserved name — pick another' : undefined}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <FieldHelp text={'The unique identifier for this workflow config.\nUse lowercase letters, numbers, and hyphens (e.g. leave-approval).\nReserved names "agent" and "auto-trigger" cannot be used — they are system-only paths.'} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontWeight: 600, fontSize: '1rem' } }}
        />

        <Select
          value={selectedVersion ?? ''}
          onChange={handleVersionChange}
          onOpen={handleVersionsOpen}
          displayEmpty
          size="small"
          disabled={versionLoadBusy}
          renderValue={(value) => {
            if (versionsLoading) return <Typography variant="body2" color="text.secondary">Loading…</Typography>;
            if (!value) return <Typography variant="body2" color="text.secondary">Select a version…</Typography>;
            const v = versions?.find((entry) => entry.version === value);
            if (!v) return `v${value}`;
            return `v${v.version} — ${v.status}${v.version !== latestVersion ? ' (not latest)' : ''}`;
          }}
          sx={{ minWidth: 170, maxWidth: 220 }}
          MenuProps={SCROLLABLE_MENU_PROPS}
        >
          {versionsLoading && (
            <MenuItem disabled value="">
              <CircularProgress size={14} sx={{ mr: 1 }} /> Loading versions…
            </MenuItem>
          )}
          {!versionsLoading && (!versions || versions.length === 0) && (
            <MenuItem disabled value="">
              No saved versions yet
            </MenuItem>
          )}
          {!versionsLoading && versions && versions.map((v) => {
            const isLatest = v.version === latestVersion;
            return (
              <MenuItem key={v.version} value={v.version}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">
                    v{v.version} — {v.status}
                    {isLatest ? (
                      <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'success.main', fontWeight: 600 }}>
                        Latest
                      </Typography>
                    ) : (
                      <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.secondary' }}>
                        Editable — saving creates a new version
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {v.created_by} · {new Date(v.created_at).toLocaleString()}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Select>

        <Tooltip title={loadDisabled && loadDisabledReason ? loadDisabledReason : ''}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoad}
              disabled={loadDisabled}
              sx={{ textTransform: 'none' }}
            >
              Load
            </Button>
          </span>
        </Tooltip>
        <Button size="small" variant="outlined" onClick={onValidate} disabled={validateDisabled} sx={{ textTransform: 'none' }}>
          Validate
        </Button>

        <Button size="small" variant="contained" onClick={onSave} disabled={saveDisabled} sx={{ textTransform: 'none' }}>
          Save
        </Button>

        <Button
          size="small"
          color="error"
          variant="outlined"
          onClick={onDeleteSelected}
          disabled={deleteSelectedDisabled}
          sx={{ textTransform: 'none' }}
        >
          Delete selected
        </Button>
      </Box>

      {workflowNamesError && <Alert severity="error" sx={{ mb: 2 }}>{workflowNamesError}</Alert>}
      {versionsError && <Alert severity="error" sx={{ mb: 2 }}>{versionsError}</Alert>}
      {oldVersionNotice && <Alert severity="info" sx={{ mb: 2 }}>{oldVersionNotice}</Alert>}
      {versionLoadWarning && <Alert severity="warning" sx={{ mb: 2 }}>{versionLoadWarning}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {validationErrors && (
        validationErrors.length === 0 ? (
          <Alert severity="success" sx={{ mb: 2 }}>Valid</Alert>
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationErrors.map((err, i) => <div key={i}>{err}</div>)}
          </Alert>
        )
      )}
      {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
    </Box>
  );
};

export default WorkflowToolbar;
